// The platform's ONE toast region (spec §3): bottom-right, above every overlay
// (--adora-z-toast), mounted once by the shell beside ConfirmHost. Hovering or focusing
// it pauses every timer. Modules never mount it; they call toast.show.
//
// data-react-aria-top-layer: while a React Aria modal is open it aria-hides everything
// outside itself and treats any outside click as "dismiss". This attribute is React
// Aria's own exemption (its UNSTABLE toast region uses it): toasts stay announced and
// clickable, and dismissing a toast does not close the modal underneath.
//
// No React Aria import: the shell imports this file directly. The Dismiss control is a
// native <button> styled with TOAST_DISMISS_CLASSES (iconButtonClasses at tone inherit, size sm; a test pins them equal), not the IconButton
// component, which would pull React Aria's Button into the shell bundle.
import { useEffect, useRef, useSyncExternalStore } from 'react';

import { XMark } from '../icons.jsx';
import { toastClasses, TOAST_DISMISS_CLASSES } from './toast-classes.js';
import { toast, toastStore } from './toast.js';
import { visibleToasts } from './toast-queue.js';

export const TOAST_REGION = 'pointer-events-none fixed right-4 bottom-4 z-(--adora-z-toast) flex w-[22rem] max-w-[calc(100vw-2rem)] flex-col items-stretch gap-2';

/**
 * The hold after the toast list changes (a dismiss unmounts the focused button, and a
 * browser may fire no blur for a removed element): hover is kept, focus is whatever is
 * actually true now. Pure, so the stuck-pause rule is testable.
 */
export function holdAfterDismiss({ hover, focusInside }) {
  return { hover: Boolean(hover), focus: Boolean(focusInside) };
}

const TONE_LABEL = { success: 'Success', danger: 'Error', warning: 'Warning', info: 'Information' };

/** The name of a toast card: its tone in words (colour alone is no meaning), e.g. "Error notification". Pure. */
export function toastLabel(tone) {
  return `${TONE_LABEL[tone] ?? 'Notification'}${TONE_LABEL[tone] ? ' notification' : ''}`;
}

/** What a screen reader hears for a toast: "Title, message". Only the toast's own text, never more. Pure. For string content; title and message may be ReactNodes, which ToastAnnouncer renders as they are. */
export function announcementText({ title, message }) {
  return [title, message].filter(Boolean).join(', ');
}

const isText = (v) => typeof v === 'string' || typeof v === 'number';

/** The announcer line's content: one string when both parts are text, otherwise the nodes themselves (never stringified). */
function announcementContent({ title, message }) {
  if ((!title || isText(title)) && (!message || isText(message))) return announcementText({ title, message });
  return <>{title}{title && message ? ', ' : null}{message}</>;
}

/**
 * The two PERSISTENT live containers (K5 I-4): polite for normal tones, assertive for danger.
 * They are mounted with the region and stay empty until a toast appears; a toast's text is
 * then ADDED to the matching one, which screen readers announce reliably (a live region that
 * is inserted together with its text often is not). The visible cards are not live regions.
 */
export function ToastAnnouncer({ state }) {
  const { visible } = visibleToasts(state);
  const line = (t) => <p key={t.id}>{announcementContent(t)}</p>;
  return (
    <>
      <div className="sr-only" aria-live="polite" aria-atomic="false">
        {visible.filter((t) => t.tone !== 'danger').map(line)}
      </div>
      <div className="sr-only" aria-live="assertive" aria-atomic="false">
        {visible.filter((t) => t.tone === 'danger').map(line)}
      </div>
    </>
  );
}

/** The toasts themselves, for a given queue state. Exported for the markup tests. */
export function ToastList({ state, onClose }) {
  const { visible, hidden } = visibleToasts(state);
  return (
    <>
      {hidden > 0 && (
        <p className="pointer-events-auto self-end rounded-full bg-sunken px-2 py-0.5 text-xs text-ink-secondary">+{hidden} more</p>
      )}
      <div className="flex flex-col gap-2">
        {visible.map((t) => {
          return (
            <div key={t.id} role="group" aria-label={toastLabel(t.tone)} className={toastClasses({ tone: t.tone })}>
              {t.icon && <span className="mt-0.5 shrink-0" aria-hidden="true">{t.icon}</span>}
              <div className="min-w-0 flex-1">
                {t.title && <p className="font-semibold">{t.title}</p>}
                {t.message && <p className="break-words text-ink-secondary">{t.message}</p>}
              </div>
              <button type="button" aria-label="Dismiss" data-toast-dismiss="" onClick={() => onClose(t.id)} className={TOAST_DISMISS_CLASSES}>
                <XMark />
              </button>
            </div>
          );
        })}
      </div>
    </>
  );
}

export default function ToastRegion() {
  const state = useSyncExternalStore(toastStore.subscribe, toastStore.snapshot, toastStore.snapshot);
  const regionRef = useRef(null);
  // Paused while EITHER the pointer is over the region OR focus is inside it.
  const hold = useRef({ hover: false, focus: false });
  const setHold = (next) => {
    const before = hold.current.hover || hold.current.focus;
    hold.current = next;
    const after = next.hover || next.focus;
    if (after && !before) toastStore.pause();
    if (!after && before) toastStore.resume();
  };
  const update = (key, value) => setHold({ ...hold.current, [key]: value });

  // Dismiss: hand focus to the next toast's Dismiss button (or the previous one) before
  // the focused button unmounts; with none left, blur cleanly. Never leave it on <body>
  // with the focus hold still set.
  const dismiss = (id) => {
    const region = regionRef.current;
    const buttons = region ? [...region.querySelectorAll('[data-toast-dismiss]')] : [];
    const i = buttons.findIndex((b) => b === document.activeElement);
    const target = i === -1 ? null : (buttons[i + 1] ?? buttons[i - 1] ?? null);
    if (target) target.focus();
    else if (i !== -1) document.activeElement.blur();
    toast.close(id);
  };

  // After the list changes, trust where focus really is, not a blur that may never have come.
  useEffect(() => {
    const region = regionRef.current;
    if (!region) return;
    setHold(holdAfterDismiss({ hover: hold.current.hover, focusInside: region.contains(document.activeElement) }));
  }, [state]);

  // Unmounting while held must not leave the store paused.
  useEffect(() => () => {
    if (hold.current.hover || hold.current.focus) {
      hold.current = { hover: false, focus: false };
      toastStore.resume();
    }
  }, []);

  // A hidden tab freezes every clock; showing it resumes them with their remaining time.
  // (No document under node: nothing to wire.)
  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const sync = () => toastStore.setTabHidden(document.hidden);
    sync();
    document.addEventListener('visibilitychange', sync);
    return () => {
      document.removeEventListener('visibilitychange', sync);
      toastStore.setTabHidden(false);
    };
  }, []);

  useEffect(() => {
    const ms = toastStore.nextDeadline();
    if (ms === null) return undefined;
    const timer = setTimeout(toastStore.expire, ms);
    return () => clearTimeout(timer);
  }, [state]);

  return (
    <section
      ref={regionRef}
      role="region"
      aria-label="Notifications"
      data-react-aria-top-layer="true"
      className={TOAST_REGION}
      onMouseEnter={() => update('hover', true)}
      onMouseLeave={() => update('hover', false)}
      onFocus={() => update('focus', true)}
      onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) update('focus', false); }}
    >
      <ToastAnnouncer state={state} />
      <ToastList state={state} onClose={dismiss} />
    </section>
  );
}
